import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Users API (for admin management)
export const usersAPI = {
  getPending: () => api.get('/users/pending'),
  getAll: () => api.get('/users/all'),
  approve: (userId) => api.post(`/users/${userId}/approve`),
  reject: (userId) => api.post(`/users/${userId}/reject`),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Invitations API
export const invitationsAPI = {
  getAll: () => api.get('/invitations'),
  create: (data) => api.post('/invitations', data),
  delete: (id) => api.delete(`/invitations/${id}`),
  validate: (code) => api.get(`/invitations/validate/${code}`),
};

// Players API
export const playersAPI = {
  getAll: () => api.get('/players'),
  getOne: (id) => api.get(`/players/${id}`),
  create: (data) => api.post('/players', data),
  update: (id, data) => api.put(`/players/${id}`, data),
  delete: (id) => api.delete(`/players/${id}`),
  uploadCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/players/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getCSVTemplate: () => api.get('/players/csv-template'),
};

// Attendance API
export const attendanceAPI = {
  getAll: (date) => api.get('/attendance', { params: { date } }),
  getByPlayer: (playerId) => api.get(`/attendance/player/${playerId}`),
  getStats: () => api.get('/attendance/stats'),
  getWeekly: () => api.get('/attendance/weekly'),
  getMonthly: () => api.get('/attendance/monthly'),
  log: (data) => api.post('/attendance', data),
  logBulk: (records) => api.post('/attendance/bulk', { records }),
  autoMarkAbsent: () => api.post('/attendance/auto-mark-absent'),
};

// Weight API
export const weightAPI = {
  getAll: (date) => api.get('/weight', { params: { date } }),
  getByPlayer: (playerId) => api.get(`/weight/player/${playerId}`),
  getStats: () => api.get('/weight/stats'),
  getWeekly: () => api.get('/weight/weekly'),
  getMonthly: () => api.get('/weight/monthly'),
  getByPosition: () => api.get('/weight/by-position'),
  log: (data) => api.post('/weight', data),
};

// Injuries API
export const injuriesAPI = {
  getAll: (status) => api.get('/injuries', { params: { status } }),
  getOne: (id) => api.get(`/injuries/${id}`),
  getByPlayer: (playerId) => api.get(`/injuries/player/${playerId}`),
  getStats: () => api.get('/injuries/stats/summary'),
  create: (data) => api.post('/injuries', data),
  update: (id, data) => api.put(`/injuries/${id}`, data),
};

// Alerts API
export const alertsAPI = {
  getAll: () => api.get('/alerts'),
  send: (data) => api.post('/alerts', data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Equipment API
export const equipmentAPI = {
  getAll: (params) => api.get('/equipment', { params }),
  getOne: (id) => api.get(`/equipment/${id}`),
  create: (data) => api.post('/equipment', data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  delete: (id) => api.delete(`/equipment/${id}`),
  checkout: (id, playerId) => api.post(`/equipment/${id}/checkout`, { player_id: playerId }),
  checkin: (id) => api.post(`/equipment/${id}/checkin`),
  getLogs: () => api.get('/equipment/logs/all'),
  getSizeReport: () => api.get('/equipment/report/sizes'),
};

// Practice Schedule API
export const practiceScheduleAPI = {
  getAll: () => api.get('/practice-schedules'),
  getOne: (id) => api.get(`/practice-schedules/${id}`),
  create: (data) => api.post('/practice-schedules', data),
  update: (id, data) => api.put(`/practice-schedules/${id}`, data),
  delete: (id) => api.delete(`/practice-schedules/${id}`),
  duplicate: (id) => api.post(`/practice-schedules/${id}/duplicate`),
  getPDF: (id) => api.get(`/practice-schedules/${id}/pdf`, { responseType: 'blob' }),
};

// Coach Check-in API
export const coachCheckinAPI = {
  setPassword: (password) => api.post('/coach-checkin/password', { password }),
  getPassword: () => api.get('/coach-checkin/password'),
  checkin: (password, position) => api.post('/coach-checkin/checkin', { password, position }),
  getTodayCheckins: () => api.get('/coach-checkin/today'),
  getMyStatus: () => api.get('/coach-checkin/status'),
  getByPosition: () => api.get('/coach-checkin/by-position'),
};

// Depth Chart API
export const depthChartAPI = {
  getAll: () => api.get('/depth-chart'),
  getOffense: () => api.get('/depth-chart/offense'),
  getDefense: () => api.get('/depth-chart/defense'),
  getPlayersByPosition: (position) => api.get(`/depth-chart/players-by-position/${position}`),
  updatePosition: (position, data) => api.put(`/depth-chart/${position}`, data),
};

// User Position API
export const userPositionAPI = {
  get: (userId) => api.get(`/users/${userId}/position`),
  update: (userId, position) => api.put(`/users/${userId}/position?position=${position}`),
};

// Sports API
export const sportsAPI = {
  getAll: () => api.get('/sports'),
  getPlayersBySport: (sport) => api.get(`/players/by-sport/${sport}`),
};

// Treatment Plans API
export const treatmentPlansAPI = {
  create: (data) => api.post('/treatment-plans', data),
  getAll: () => api.get('/treatment-plans'),
  getForPlayer: (playerId) => api.get(`/treatment-plans/player/${playerId}`),
  get: (planId) => api.get(`/treatment-plans/${planId}`),
  update: (planId, data) => api.put(`/treatment-plans/${planId}`, data),
  delete: (planId) => api.delete(`/treatment-plans/${planId}`),
};

// Rehab Check-in API
export const rehabAPI = {
  start: (treatmentPlanId) => api.post('/rehab-checkins/start', { treatment_plan_id: treatmentPlanId }),
  complete: (checkinId, data) => api.post(`/rehab-checkins/${checkinId}/complete`, data),
  getTodayAll: () => api.get('/rehab-checkins/today'),
  getForPlayer: (playerId) => api.get(`/rehab-checkins/player/${playerId}`),
  getPlayerToday: (playerId) => api.get(`/rehab-checkins/player/${playerId}/today`),
  getSummary: () => api.get('/rehab-checkins/summary'),
};

export default api;
